import type { WireReadyPanel } from 'features/charts-v2/types/chart-query-panel'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'

/**
 * Marker key the BE checks for to route the search request through the
 * vector-search expression. See `"SEMANTIC" in search_criteria` dispatch at
 * `api/lib/analytics/value_objects/expression/search_function.py`. The
 * empty-array value is intentional: the parser's `searchCriteria.keys()`
 * projection lifts this into the IR's `search_criteria` tuple, and the
 * `_build_search_terms_logical` step ignores entries with empty values.
 *
 * Exported because the marker has TWO directions now: it is written here on
 * the way out and lifted back into `smartSearchEnabled` on the way in. One
 * constant, so the pair cannot drift onto two spellings of one string.
 */
export const SEMANTIC_MARKER = 'SEMANTIC'

/**
 * Inject the SEMANTIC routing marker into the outgoing panel's searchCriteria
 * when the user has smart search enabled with at least one term. Returns the
 * input panel untouched when not applicable; otherwise returns a shallow-cloned
 * panel with a shallow-cloned searchCriteria so the caller's state is never
 * mutated.
 *
 * Idempotent: re-injecting over an existing `SEMANTIC: []` is a no-op.
 */
export function injectSemanticIntoPanel(panel: ChartPanel): WireReadyPanel {
  const hasTerm = panel.searchTerms.some((t) => t && t.trim().length > 0)
  if (!panel.smartSearchEnabled || !hasTerm) {
    return panel as WireReadyPanel
  }
  // Through a `ChartPanel` first: an object literal does not structurally
  // overlap the brand, so TS rejects the direct cast.
  const injected: ChartPanel = {
    ...panel,
    searchCriteria: {
      ...panel.searchCriteria,
      [SEMANTIC_MARKER]: [],
    },
  }
  return injected as WireReadyPanel
}
